import {
  Action,
  createEmptyActionState,
  type ActionState,
} from "./actions";

const KEY_BINDINGS: Record<string, Action> = {
  ArrowLeft: Action.Left,
  KeyA: Action.Left,
  ArrowRight: Action.Right,
  KeyD: Action.Right,
  ArrowUp: Action.Jump,
  Space: Action.Jump,
  KeyW: Action.Jump,
  Enter: Action.Confirm,
  KeyZ: Action.Confirm,
  Escape: Action.Cancel,
};

/**
 * Keyboard → action bus. Phaser scenes poll `getState()` each frame.
 * Mobile stubs can call `setVirtual(action, down)` into the same bus later.
 */
export class InputController {
  private readonly held = createEmptyActionState();
  private readonly pressed = createEmptyActionState();
  private readonly virtual = createEmptyActionState();
  private attached = false;

  attach(target: Window = window): void {
    if (this.attached) return;
    this.attached = true;
    target.addEventListener("keydown", this.onKeyDown);
    target.addEventListener("keyup", this.onKeyUp);
  }

  detach(target: Window = window): void {
    if (!this.attached) return;
    this.attached = false;
    target.removeEventListener("keydown", this.onKeyDown);
    target.removeEventListener("keyup", this.onKeyUp);
  }

  setVirtual(action: Action, down: boolean): void {
    const wasDown = this.virtual[action];
    this.virtual[action] = down;
    if (down && !wasDown) this.pressed[action] = true;
  }

  /** Edge-triggered presses since last clearPressed(). */
  consumePressed(): ActionState {
    const snapshot = { ...this.pressed };
    for (const key of Object.keys(this.pressed) as Action[]) {
      this.pressed[key] = false;
    }
    return snapshot;
  }

  getState(): ActionState {
    return {
      left: this.held.left || this.virtual.left,
      right: this.held.right || this.virtual.right,
      jump: this.held.jump || this.virtual.jump,
      confirm: this.held.confirm || this.virtual.confirm,
      cancel: this.held.cancel || this.virtual.cancel,
    };
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    const action = KEY_BINDINGS[event.code];
    if (!action) return;
    this.held[action] = true;
    this.pressed[action] = true;
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    const action = KEY_BINDINGS[event.code];
    if (!action) return;
    this.held[action] = false;
  };
}

export const sharedInput = new InputController();
